/**
 * Function List - Searchable/filterable table of all functions
 * Issue #462: Secondary view for finding specific functions
 */

import { useState, useMemo } from 'react';
import type { CallGraphData, FunctionNodeType } from '@veodiagram/shared';

interface FunctionListProps {
  data: CallGraphData;
  onSelectFunction: (functionId: string) => void;
  selectedFunctionId?: string;
  onViewInExplorer?: (functionId: string) => void;
  onViewSubgraph?: (functionId: string) => void;
}

type SortField = 'name' | 'module' | 'type' | 'callers' | 'callees';
type SortDirection = 'asc' | 'desc';

export function FunctionList({ data, onSelectFunction, selectedFunctionId, onViewInExplorer, onViewSubgraph }: FunctionListProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<FunctionNodeType | 'all'>('all');
  const [sortField, setSortField] = useState<SortField>('callers');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [moduleFilter, setModuleFilter] = useState('');

  // Build caller/callee counts
  const { callerCounts, calleeCounts } = useMemo(() => {
    const callerCounts = new Map<string, number>();
    const calleeCounts = new Map<string, number>();

    for (const edge of data.edges) {
      calleeCounts.set(edge.source, (calleeCounts.get(edge.source) || 0) + 1);
      callerCounts.set(edge.target, (callerCounts.get(edge.target) || 0) + 1);
    }

    return { callerCounts, calleeCounts };
  }, [data.edges]);

  // Get unique modules for filter dropdown
  const modules = useMemo(() => {
    const moduleSet = new Set<string>();
    for (const node of data.nodes) {
      moduleSet.add(node.module);
    }
    return Array.from(moduleSet).sort();
  }, [data.nodes]);

  // Filter and sort nodes
  const filteredNodes = useMemo(() => {
    let nodes = [...data.nodes];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      nodes = nodes.filter(node =>
        node.name.toLowerCase().includes(query) ||
        node.qualifiedName.toLowerCase().includes(query) ||
        node.module.toLowerCase().includes(query)
      );
    }

    // Apply type filter
    if (typeFilter !== 'all') {
      nodes = nodes.filter(node => node.type === typeFilter);
    }

    // Apply module filter
    if (moduleFilter) {
      nodes = nodes.filter(node => node.module === moduleFilter);
    }

    // Sort
    nodes.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'module':
          comparison = a.module.localeCompare(b.module);
          break;
        case 'type':
          comparison = a.type.localeCompare(b.type);
          break;
        case 'callers':
          comparison = (callerCounts.get(a.id) || 0) - (callerCounts.get(b.id) || 0);
          break;
        case 'callees':
          comparison = (calleeCounts.get(a.id) || 0) - (calleeCounts.get(b.id) || 0);
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return nodes;
  }, [data.nodes, searchQuery, typeFilter, moduleFilter, sortField, sortDirection, callerCounts, calleeCounts]);

  // Handle sort click
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Get sort indicator
  const getSortIndicator = (field: SortField) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? ' ^' : ' v';
  };

  // Get type badge color
  const getTypeBadge = (type: FunctionNodeType) => {
    const colors: Record<FunctionNodeType, string> = {
      entry: 'bg-green-100 text-green-800',
      function: 'bg-blue-100 text-blue-800',
      method: 'bg-purple-100 text-purple-800',
      arrow: 'bg-pink-100 text-pink-800',
      constructor: 'bg-amber-100 text-amber-800',
      external: 'bg-red-100 text-red-800',
      critical: 'bg-red-200 text-red-900',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900">
      {/* Filters Row */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 space-y-3">
        {/* Search */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search functions by name, qualified name, or module..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
          />
          <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>

        {/* Filter dropdowns */}
        <div className="flex gap-4">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as FunctionNodeType | 'all')}
            className="px-3 py-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white"
          >
            <option value="all">All Types</option>
            <option value="entry">Entry Points</option>
            <option value="function">Functions</option>
            <option value="method">Methods</option>
            <option value="arrow">Arrow Functions</option>
            <option value="constructor">Constructors</option>
            <option value="external">External</option>
          </select>

          <select
            value={moduleFilter}
            onChange={(e) => setModuleFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white max-w-xs"
          >
            <option value="">All Modules</option>
            {modules.map(module => (
              <option key={module} value={module}>{module}</option>
            ))}
          </select>

          <div className="flex-1" />

          <span className="self-center text-sm text-gray-500 dark:text-gray-400">
            {filteredNodes.length} of {data.nodes.length} functions
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
            <tr>
              <th
                onClick={() => handleSort('type')}
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Type{getSortIndicator('type')}
              </th>
              <th
                onClick={() => handleSort('name')}
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Function{getSortIndicator('name')}
              </th>
              <th
                onClick={() => handleSort('module')}
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Module{getSortIndicator('module')}
              </th>
              <th
                onClick={() => handleSort('callers')}
                className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Callers{getSortIndicator('callers')}
              </th>
              <th
                onClick={() => handleSort('callees')}
                className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Callees{getSortIndicator('callees')}
              </th>
              {(onViewInExplorer || onViewSubgraph) && (
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredNodes.map(node => {
              const callers = callerCounts.get(node.id) || 0;
              const callees = calleeCounts.get(node.id) || 0;
              const isSelected = node.id === selectedFunctionId;

              return (
                <tr
                  key={node.id}
                  onClick={() => onSelectFunction(node.id)}
                  className={`cursor-pointer transition-colors ${
                    isSelected
                      ? 'bg-blue-50 dark:bg-blue-900/30'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${getTypeBadge(node.type)}`}>
                      {node.type}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 dark:text-white">{node.name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{node.qualifiedName}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                    {node.module}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`font-medium ${callers > 5 ? 'text-green-600' : 'text-gray-600 dark:text-gray-300'}`}>
                      {callers}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`font-medium ${callees > 10 ? 'text-blue-600' : 'text-gray-600 dark:text-gray-300'}`}>
                      {callees}
                    </span>
                  </td>
                  {(onViewInExplorer || onViewSubgraph) && (
                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-2 justify-end">
                        {onViewInExplorer && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onViewInExplorer(node.id);
                            }}
                            className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 dark:bg-blue-900/50 dark:text-blue-300 dark:hover:bg-blue-800/50"
                            title="View in Explorer"
                          >
                            Explorer
                          </button>
                        )}
                        {onViewSubgraph && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onViewSubgraph(node.id);
                            }}
                            className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 dark:bg-purple-900/50 dark:text-purple-300 dark:hover:bg-purple-800/50"
                            title="View Subgraph"
                          >
                            Subgraph
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>

        {filteredNodes.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
            <p>No functions match your filters</p>
            <button
              onClick={() => {
                setSearchQuery('');
                setTypeFilter('all');
                setModuleFilter('');
              }}
              className="mt-2 text-blue-600 hover:underline"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default FunctionList;
